import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar,
  Clock,
  Plus,
  Play,
  RotateCw,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  History,
  Eye,
  Download,
  Search,
  Building,
  Users,
  ShieldAlert,
  Info,
  CalendarDays,
  Sparkles,
} from 'lucide-react';
import { downloadCSV } from '../lib/exportUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PermissionCreditCalendar({ selectedCompany }) {
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCompanyId, setActiveCompanyId] = useState(() => {
    if (selectedCompany) return selectedCompany;
    try {
      const stored = localStorage.getItem('selectedCompany');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id || parsed?.company_id) return parsed.id || parsed.company_id;
      }
    } catch { /* ignore */ }
    return '';
  });
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Modals state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewSchedule, setPreviewSchedule] = useState([]);
  const [previewCalName, setPreviewCalName] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState([]);
  const [showRunAccrualModal, setShowRunAccrualModal] = useState(false);
  const [accrualRunning, setAccrualRunning] = useState(false);
  const [accrualTargetDate, setAccrualTargetDate] = useState(new Date().toISOString().split('T')[0]);
  const [accrualRunResult, setAccrualRunResult] = useState(null);

  // Employee Accrual & Balance Report state
  const [reportRows, setReportRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportSearch, setReportSearch] = useState('');
  const [reportCycleKey, setReportCycleKey] = useState('');

  // Form State
  const initialForm = {
    company_id: selectedCompany || '',
    calendar_name: '',
    applicable_audience: 'all',
    applicable_group: 'all',
    branch_id: '',
    department_id: '',
    accrual_start_date: new Date().toISOString().split('T')[0],
    is_ongoing: true,
    accrual_end_date: '',
    cycle_start_day: 15,
    cycle_end_day: 14,
    credit_day: 15,
    credit_amount: 2.0,
    credit_unit: 'permissions',
    inherit_policy_amount: false,
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: '',
    is_active: true,
    notes: '',
    change_reason: '',
  };
  const [form, setForm] = useState(initialForm);

  // Sync selectedCompany prop
  useEffect(() => {
    if (selectedCompany) {
      setActiveCompanyId(selectedCompany);
      setForm((prev) => ({ ...prev, company_id: selectedCompany }));
    }
  }, [selectedCompany]);

  // Fetch Companies, Branches, Departments
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [compRes, branchRes, deptRes] = await Promise.allSettled([
          axios.get(`${API_URL}/companies`, { withCredentials: true }),
          axios.get(`${API_URL}/branches`, { withCredentials: true }),
          axios.get(`${API_URL}/departments`, { withCredentials: true }),
        ]);
        if (compRes.status === 'fulfilled') {
          const cData = compRes.value.data;
          const compList = Array.isArray(cData) ? cData : (cData?.companies || []);
          setCompanies(compList);
          setActiveCompanyId((prev) => {
            if (prev) return prev;
            if (selectedCompany) return selectedCompany;
            return compList[0]?.id || compList[0]?._id || '';
          });
        }
        if (branchRes.status === 'fulfilled') {
          const bData = branchRes.value.data;
          setBranches(Array.isArray(bData) ? bData : (bData?.data || []));
        }
        if (deptRes.status === 'fulfilled') {
          const dData = deptRes.value.data;
          setDepartments(Array.isArray(dData) ? dData : (dData?.data || []));
        }
      } catch (err) {
        console.error('Failed to fetch calendar metadata:', err);
      }
    };
    fetchMetadata();
  }, [selectedCompany]);

  // Fetch Calendars for active company
  const fetchCalendars = useCallback(async () => {
    const compId = activeCompanyId || selectedCompany;
    if (!compId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/permission-calendar?company_id=${encodeURIComponent(compId)}`, {
        withCredentials: true,
      });
      const list = res.data.calendars || res.data.data || (Array.isArray(res.data) ? res.data : []);
      setCalendars(list);
    } catch (err) {
      console.error('Error fetching permission calendars:', err);
      toast.error('Failed to load permission credit calendars');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, selectedCompany]);

  // Fetch Accrual Report
  const fetchAccrualReport = useCallback(async () => {
    const compId = activeCompanyId || selectedCompany;
    if (!compId) return;
    setReportLoading(true);
    try {
      let url = `${API_URL}/permission-calendar/accrual-history?company_id=${encodeURIComponent(compId)}`;
      if (reportCycleKey) url += `&cycle_key=${encodeURIComponent(reportCycleKey)}`;
      const res = await axios.get(url, { withCredentials: true });
      const rows = res.data.transactions || res.data.data || (Array.isArray(res.data) ? res.data : []);
      setReportRows(rows);
    } catch (err) {
      console.error('Error fetching accrual history:', err);
    } finally {
      setReportLoading(false);
    }
  }, [activeCompanyId, selectedCompany, reportCycleKey]);

  useEffect(() => {
    fetchCalendars();
    fetchAccrualReport();
  }, [fetchCalendars, fetchAccrualReport]);

  // Open Create Modal
  const handleOpenCreate = () => {
    setEditingCalendar(null);
    const targetComp = activeCompanyId || selectedCompany || companies[0]?.id || companies[0]?._id || '';
    setForm({
      ...initialForm,
      company_id: targetComp,
    });
    setShowConfigModal(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (cal) => {
    setEditingCalendar(cal);
    setForm({
      company_id: cal.company_id || activeCompanyId || selectedCompany,
      calendar_name: cal.calendar_name || '',
      applicable_audience: cal.applicable_audience || 'all',
      applicable_group: cal.applicable_group || 'all',
      branch_id: cal.branch_id || '',
      department_id: cal.department_id || '',
      accrual_start_date: cal.accrual_start_date || '',
      is_ongoing: cal.is_ongoing !== undefined ? cal.is_ongoing : !cal.accrual_end_date,
      accrual_end_date: cal.accrual_end_date || '',
      cycle_start_day: cal.cycle_start_day !== undefined ? cal.cycle_start_day : 15,
      cycle_end_day: cal.cycle_end_day !== undefined ? cal.cycle_end_day : 14,
      credit_day: cal.credit_day !== undefined ? cal.credit_day : 15,
      credit_amount: cal.credit_amount !== undefined ? cal.credit_amount : 2.0,
      credit_unit: cal.credit_unit || 'permissions',
      inherit_policy_amount: cal.inherit_policy_amount !== undefined ? cal.inherit_policy_amount : true,
      effective_from: cal.effective_from || '',
      effective_to: cal.effective_to || '',
      is_active: cal.is_active !== undefined ? cal.is_active : true,
      notes: cal.notes || '',
      change_reason: '',
    });
    setShowConfigModal(true);
  };

  // Save Calendar (Create or Update)
  const handleSaveCalendar = async (e) => {
    e.preventDefault();
    if (!form.calendar_name.trim()) {
      toast.error('Please enter a Calendar Name');
      return;
    }
    if (!form.accrual_start_date) {
      toast.error('Please select an Accrual Start Date');
      return;
    }
    if (!form.is_ongoing && form.accrual_end_date && form.accrual_start_date > form.accrual_end_date) {
      toast.error('Accrual Start Date cannot be after Accrual End Date');
      return;
    }
    if (form.effective_to && form.effective_from && form.effective_from > form.effective_to) {
      toast.error('Effective From Date cannot be after Effective To Date');
      return;
    }
    if (editingCalendar && editingCalendar.is_active && !form.change_reason.trim()) {
      toast.error('Please provide a Change Reason when updating an active calendar configuration');
      return;
    }

    try {
      const payload = {
        ...form,
        cycle_start_day: parseInt(form.cycle_start_day, 10),
        cycle_end_day: parseInt(form.cycle_end_day, 10),
        credit_day: parseInt(form.credit_day, 10),
        credit_amount: parseFloat(form.credit_amount),
        accrual_end_date: form.is_ongoing ? null : form.accrual_end_date || null,
        effective_to: form.effective_to || null,
      };

      const calId = editingCalendar?.id || editingCalendar?._id;
      if (editingCalendar && calId) {
        await axios.put(`${API_URL}/permission-calendar/${calId}`, payload, {
          withCredentials: true,
        });
        toast.success('Permission Credit Calendar updated successfully');
      } else {
        await axios.post(`${API_URL}/permission-calendar`, payload, {
          withCredentials: true,
        });
        toast.success('Permission Credit Calendar created successfully');
      }
      setShowConfigModal(false);
      if (payload.company_id && payload.company_id !== activeCompanyId) {
        setActiveCompanyId(payload.company_id);
      }
      await fetchCalendars();
      await fetchAccrualReport();
    } catch (err) {
      console.error('Error saving calendar:', err);
      toast.error(err.response?.data?.detail || 'Failed to save calendar');
    }
  };

  // Toggle Active Status
  const handleToggleActive = async (cal) => {
    const actionText = cal.is_active ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${actionText} "${cal.calendar_name}"?`)) {
      return;
    }
    const calId = cal.id || cal._id;
    try {
      await axios.patch(`${API_URL}/permission-calendar/${calId}/activate`, {}, {
        withCredentials: true,
      });
      toast.success(`Calendar ${cal.is_active ? 'deactivated' : 'activated'} successfully`);
      await fetchCalendars();
      await fetchAccrualReport();
    } catch (err) {
      console.error('Error toggling status:', err);
      toast.error(err.response?.data?.detail || 'Failed to update calendar status');
    }
  };

  // Delete Calendar
  const handleDeleteCalendar = async (cal) => {
    if (!window.confirm(`Are you sure you want to delete "${cal.calendar_name}"? This action cannot be undone.`)) {
      return;
    }
    const calId = cal.id || cal._id;
    try {
      await axios.delete(`${API_URL}/permission-calendar/${calId}`, {
        withCredentials: true,
      });
      toast.success('Calendar deleted successfully');
      await fetchCalendars();
      await fetchAccrualReport();
    } catch (err) {
      console.error('Error deleting calendar:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete calendar');
    }
  };

  // Open Preview Modal
  const handleOpenPreview = async (cal) => {
    setPreviewCalName(cal.calendar_name);
    const calId = cal.id || cal._id;
    try {
      const res = await axios.get(`${API_URL}/permission-calendar/schedule-preview?calendar_id=${calId}&count=6`, {
        withCredentials: true,
      });
      setPreviewSchedule(res.data.schedule || res.data.data || []);
      setShowPreviewModal(true);
    } catch (err) {
      console.error('Error fetching preview:', err);
      toast.error('Failed to load schedule preview');
    }
  };

  // Open History Modal
  const handleOpenHistory = (cal) => {
    setSelectedHistory(cal.change_history || []);
    setPreviewCalName(cal.calendar_name);
    setShowHistoryModal(true);
  };

  // Execute Accrual
  const handleExecuteAccrual = async () => {
    const compId = activeCompanyId || selectedCompany;
    if (!compId) {
      toast.error('Please select a company first');
      return;
    }
    setAccrualRunning(true);
    setAccrualRunResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/permission-calendar/accrual/run?company_id=${encodeURIComponent(compId)}&cycle_date=${encodeURIComponent(accrualTargetDate)}`,
        {
          company_id: compId,
          cycle_date: accrualTargetDate,
        },
        { withCredentials: true }
      );

      setAccrualRunResult(res.data);
      if (res.data.success) {
        toast.success(res.data.message || 'Permission accrual executed successfully');
        await fetchAccrualReport();
      } else {
        toast.warning(res.data.message || 'Accrual was not processed');
      }
    } catch (err) {
      console.error('Error running accrual:', err);
      toast.error(err.response?.data?.detail || 'Failed to execute permission accrual');
    } finally {
      setAccrualRunning(false);
    }
  };

  // Export Accrual Report CSV
  const handleExportReport = () => {
    if (reportRows.length === 0) {
      toast.error('No accrual history records to export');
      return;
    }
    const exportData = reportRows.map((r) => ({
      Employee_ID: r.employee_code || r.employee_id || '',
      Employee_Name: r.employee_name || '',
      Cycle_Key: r.cycle_key || '',
      Cycle_Start: r.cycle_start_date || '',
      Cycle_End: r.cycle_end_date || '',
      Credited_Amount: r.credited_amount || 0,
      Consumed_Amount: r.consumed_amount || 0,
      Remaining_Balance: r.remaining_balance || 0,
      Credit_Date: r.last_credit_date || '',
      Calendar_Name: r.calendar_name || '',
    }));
    downloadCSV(exportData, `permission_credit_accrual_${activeCompanyId}.csv`);
    toast.success('Permission credit accrual report exported');
  };

  // Filter report rows
  const filteredReportRows = reportRows.filter((row) => {
    if (!reportSearch) return true;
    const s = reportSearch.toLowerCase();
    return (
      (row.employee_name && row.employee_name.toLowerCase().includes(s)) ||
      (row.employee_code && row.employee_code.toLowerCase().includes(s)) ||
      (row.cycle_key && row.cycle_key.toLowerCase().includes(s))
    );
  });

  const activeCalendar = calendars.find((c) => c.is_active);
  const activeCompany = companies.find((c) => String(c.id || c._id) === String(activeCompanyId));
  const activeCompanyName = activeCompany?.name || activeCompany?.company_name || '';

  return (
    <div className="space-y-6">
      {/* Top Banner Card */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-800 to-slate-900 rounded-2xl p-6 text-white shadow-md relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 opacity-10 flex items-center pr-8 pointer-events-none">
          <CalendarDays className="w-64 h-64 text-white" />
        </div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1.5 max-w-2xl">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/30 border border-blue-400/30 text-blue-200">
                Leave &amp; Attendance Policy
              </span>
              {activeCompanyName && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/10 border border-white/20 text-white flex items-center gap-1">
                  <Building className="w-3 h-3" /> {activeCompanyName}
                </span>
              )}
              {activeCalendar ? (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Active Calendar Configured
                </span>
              ) : (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/20 border border-amber-400/40 text-amber-300 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Default Policy Fallback (1st–End)
                </span>
              )}
            </div>
            <h2 className="text-xl font-bold tracking-tight text-white flex items-center gap-2">
              Company Permission Credit Calendar
            </h2>
            <p className="text-sm text-slate-300 leading-relaxed">
              Define custom monthly credit cycles (e.g. 15th to 14th), scheduled accrual dates, and exact permission amounts per employee. 
              Credits automatically accrue once per cycle and integrate with permission requests.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              onClick={() => {
                setAccrualRunResult(null);
                setShowRunAccrualModal(true);
              }}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-sm"
              title="Manually trigger permission credit accrual for the active calendar cycle"
            >
              <Play className="w-4 h-4" /> Run Accrual Now
            </button>
            <button
              onClick={handleOpenCreate}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#0052FF] hover:bg-blue-600 active:bg-blue-700 text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-sm"
            >
              <Plus className="w-4 h-4" /> Configure Calendar
            </button>
          </div>
        </div>

        {/* Quick Cycle Summary if Active */}
        {activeCalendar && (
          <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div className="bg-white/10 backdrop-blur rounded-lg p-2.5">
              <span className="text-slate-300 block">Cycle Range</span>
              <span className="font-semibold text-white text-sm">
                Day {activeCalendar.cycle_start_day} to Day {activeCalendar.cycle_end_day || 'End'}
              </span>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-2.5">
              <span className="text-slate-300 block">Monthly Credit Date</span>
              <span className="font-semibold text-white text-sm">
                Day {activeCalendar.credit_day || activeCalendar.cycle_start_day} of month
              </span>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-2.5">
              <span className="text-slate-300 block">Credit Amount</span>
              <span className="font-semibold text-white text-sm">
                {activeCalendar.credit_amount} {activeCalendar.credit_unit || 'Permissions'}/mo
              </span>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-2.5">
              <span className="text-slate-300 block">Accrual Period</span>
              <span className="font-semibold text-white text-sm">
                {activeCalendar.accrual_start_date || 'N/A'} → {activeCalendar.accrual_end_date || 'Ongoing'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Main Configurations List */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#0052FF]" />
              Configured Permission Calendars
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Each company can maintain distinct credit cycles. Inactive or expired policies will not accrue credits.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {companies.length > 1 && (
              <select
                value={activeCompanyId}
                onChange={(e) => setActiveCompanyId(e.target.value)}
                className="border border-slate-200 rounded-lg px-2.5 py-1 text-xs bg-white text-slate-700 font-medium"
                title="Filter by Company"
              >
                {companies.map((c) => (
                  <option key={c.id || c._id} value={c.id || c._id}>
                    {c.name || c.company_name}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => {
                fetchCalendars();
                fetchAccrualReport();
              }}
              className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              title="Refresh calendars list"
            >
              <RotateCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading permission calendars...</div>
        ) : calendars.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-blue-50 text-[#0052FF] mx-auto flex items-center justify-center">
              <Calendar className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-slate-700">No Custom Permission Credit Calendar Configured</p>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              This company currently operates under the default standard cycle (1st of month to end of month).
              Click "Configure Calendar" above to establish custom mid-month cycles (e.g. 15th to 14th).
            </p>
            <button
              onClick={handleOpenCreate}
              className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 bg-[#0052FF] text-white rounded-lg text-xs font-semibold hover:bg-blue-600"
            >
              <Plus className="w-3.5 h-3.5" /> Configure Calendar Now
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-600 text-xs font-semibold uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Calendar Name &amp; Audience</th>
                  <th className="px-4 py-3">Credit Cycle</th>
                  <th className="px-4 py-3">Credit Schedule</th>
                  <th className="px-4 py-3">Accrual Period</th>
                  <th className="px-4 py-3">Effective Range</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {calendars.map((cal) => {
                  const isActive = cal.is_active;
                  return (
                    <tr
                      key={cal.id}
                      className={`transition-colors ${
                        isActive ? 'bg-blue-50/30 hover:bg-blue-50/60' : 'hover:bg-slate-50/70 text-slate-500'
                      }`}
                    >
                      <td className="px-4 py-3.5">
                        <div className="font-semibold text-slate-800 flex items-center gap-2">
                          {cal.calendar_name}
                          {isActive && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 uppercase tracking-wider">
                              ACTIVE
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                          <span className="capitalize">
                            Audience: {cal.applicable_group || 'All Employees'}
                          </span>
                          {cal.notes && (
                            <span className="text-slate-400 italic truncate max-w-xs" title={cal.notes}>
                              • {cal.notes}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3.5 font-medium text-slate-700">
                        <div>
                          {cal.cycle_start_day <= 1 ? (
                            '1st to End of Month'
                          ) : (
                            `${cal.cycle_start_day}th to ${cal.cycle_end_day || cal.cycle_start_day - 1}th (Following)`
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          Credited on day {cal.credit_day || cal.cycle_start_day}
                        </div>
                      </td>

                      <td className="px-4 py-3.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold">
                          {cal.credit_amount} {cal.credit_unit || 'Permissions'} / cycle
                        </span>
                      </td>

                      <td className="px-4 py-3.5 text-xs text-slate-600">
                        <div>From: <span className="font-medium">{cal.accrual_start_date || 'N/A'}</span></div>
                        <div>To: <span className="font-medium">{cal.accrual_end_date || 'Ongoing'}</span></div>
                      </td>

                      <td className="px-4 py-3.5 text-xs text-slate-600">
                        <div>From: <span className="font-medium">{cal.effective_from || 'Always'}</span></div>
                        <div>To: <span className="font-medium">{cal.effective_to || 'Always'}</span></div>
                      </td>

                      <td className="px-4 py-3.5">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${
                            isActive
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {isActive ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      <td className="px-4 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleOpenPreview(cal)}
                            className="p-1.5 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Preview Upcoming Credit Schedule"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenHistory(cal)}
                            className="p-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                            title="View Audit Change History"
                          >
                            <History className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(cal)}
                            className={`px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
                              isActive
                                ? 'border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100'
                                : 'border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                            }`}
                            title={isActive ? 'Deactivate Calendar' : 'Set as Active Calendar'}
                          >
                            {isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleOpenEdit(cal)}
                            className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit Calendar Settings"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCalendar(cal)}
                            className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Calendar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Employee Accrual & Balance History Report */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-600" />
              Employee Permission Accrual &amp; Balance Register
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Live audit record of monthly permission credits, approved usage, and remaining cycle balances.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search employee or cycle..."
                value={reportSearch}
                onChange={(e) => setReportSearch(e.target.value)}
                className="pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs w-56 focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
              />
            </div>
            <button
              onClick={handleExportReport}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Export Register CSV
            </button>
            <button
              onClick={fetchAccrualReport}
              className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              title="Refresh Register"
            >
              <RotateCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {reportLoading ? (
          <div className="p-10 text-center text-slate-400">Loading accrual report...</div>
        ) : filteredReportRows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">
            No accrual transactions found for this company. Trigger "Run Accrual Now" to issue credits for the current cycle.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-600 text-xs font-semibold uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Accrual Cycle</th>
                  <th className="px-4 py-3 text-center">Credited</th>
                  <th className="px-4 py-3 text-center">Consumed (Approved)</th>
                  <th className="px-4 py-3 text-center">Remaining Balance</th>
                  <th className="px-4 py-3">Credit Date</th>
                  <th className="px-4 py-3">Source Policy</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredReportRows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50/70">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{r.employee_name || 'Employee'}</div>
                      <div className="text-xs text-slate-400 font-mono">{r.employee_code || r.employee_id}</div>
                    </td>

                    <td className="px-4 py-3 text-xs">
                      <span className="font-semibold text-slate-700 block">{r.cycle_key}</span>
                      <span className="text-slate-400">
                        {r.cycle_start_date} → {r.cycle_end_date}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold text-xs border border-blue-200">
                        {r.credited_amount} {r.unit || 'perm'}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-center font-medium text-slate-600">
                      {r.consumed_amount || 0}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                          (r.remaining_balance || 0) > 0
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {r.remaining_balance || 0}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-xs text-slate-600">
                      {r.last_credit_date ? r.last_credit_date.slice(0, 10) : 'N/A'}
                    </td>

                    <td className="px-4 py-3 text-xs text-slate-500">
                      {r.calendar_name || 'Standard Calendar'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal 1: Add / Edit Calendar Configuration */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto border border-slate-100">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {editingCalendar ? 'Edit Permission Credit Calendar' : 'New Permission Credit Calendar'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Configure company-specific permission credit start/end dates and monthly accrual cycles.
                </p>
              </div>
              <button
                onClick={() => setShowConfigModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveCalendar} className="p-6 space-y-5">
              {/* Basic Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Calendar Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Mid-Month 15th-14th Calendar"
                    value={form.calendar_name}
                    onChange={(e) => setForm({ ...form, calendar_name: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0052FF] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Target Company *
                  </label>
                  <select
                    value={form.company_id}
                    onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#0052FF] focus:outline-none"
                  >
                    {companies.map((c) => (
                      <option key={c.id || c._id} value={c.id || c._id}>
                        {c.name || c.company_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Audience Targeting */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                <label className="block text-xs font-bold text-slate-700 uppercase">
                  Applicable Employee Group
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'all', label: 'All Employees' },
                    { id: 'branch', label: 'Specific Branch' },
                    { id: 'department', label: 'Specific Department' },
                  ].map((aud) => (
                    <button
                      key={aud.id}
                      type="button"
                      onClick={() => setForm({ ...form, applicable_group: aud.id })}
                      className={`py-2 px-3 rounded-lg text-xs font-medium border text-center transition-all ${
                        form.applicable_group === aud.id
                          ? 'bg-[#0052FF] text-white border-[#0052FF] shadow-sm'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {aud.label}
                    </button>
                  ))}
                </div>

                {form.applicable_group === 'branch' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Select Branch</label>
                    <select
                      value={form.branch_id}
                      onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                      required
                    >
                      <option value="">-- Choose Branch --</option>
                      {branches.map((b) => (
                        <option key={b.id || b._id} value={b.id || b._id}>
                          {b.name || b.branch_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {form.applicable_group === 'department' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Select Department</label>
                    <select
                      value={form.department_id}
                      onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                      required
                    >
                      <option value="">-- Choose Department --</option>
                      {departments.map((d) => (
                        <option key={d.id || d._id} value={d.id || d._id}>
                          {d.name || d.department_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Accrual Boundaries */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Accrual Start Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={form.accrual_start_date}
                    onChange={(e) => setForm({ ...form, accrual_start_date: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <span className="text-[11px] text-slate-400">Date from which permission credits begin</span>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold text-slate-700 uppercase">
                      Accrual End Date
                    </label>
                    <label className="inline-flex items-center gap-1 text-xs text-[#0052FF] font-medium cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.is_ongoing}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            is_ongoing: e.target.checked,
                            accrual_end_date: e.target.checked ? '' : form.accrual_end_date,
                          })
                        }
                        className="rounded border-slate-300 text-[#0052FF] focus:ring-0"
                      />
                      Ongoing Period
                    </label>
                  </div>
                  <input
                    type="date"
                    disabled={form.is_ongoing}
                    value={form.accrual_end_date}
                    onChange={(e) => setForm({ ...form, accrual_end_date: e.target.value })}
                    className={`w-full border rounded-lg px-3 py-2 text-sm ${
                      form.is_ongoing
                        ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                        : 'border-slate-300'
                    }`}
                  />
                  <span className="text-[11px] text-slate-400">
                    {form.is_ongoing ? 'Ongoing with no fixed end date' : 'Credits will not accrue after this date'}
                  </span>
                </div>
              </div>

              {/* Monthly Credit Cycle Configuration */}
              <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-200 space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#0052FF]" />
                  <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider">
                    Monthly Credit Cycle Definition
                  </h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Cycle Start Day *
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      required
                      value={form.cycle_start_day}
                      onChange={(e) => setForm({ ...form, cycle_start_day: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                    />
                    <span className="text-[10px] text-slate-500">e.g. 15 for 15th of month</span>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Cycle End Day *
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      required
                      value={form.cycle_end_day}
                      onChange={(e) => setForm({ ...form, cycle_end_day: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                    />
                    <span className="text-[10px] text-slate-500">e.g. 14 for 14th of next month</span>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Monthly Credit Day *
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      required
                      value={form.credit_day}
                      onChange={(e) => setForm({ ...form, credit_day: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                    />
                    <span className="text-[10px] text-slate-500">Day credits are granted</span>
                  </div>
                </div>

                <div className="text-xs text-blue-800 bg-blue-100/60 p-2.5 rounded-lg flex items-start gap-2">
                  <Info className="w-4 h-4 text-[#0052FF] shrink-0 mt-0.5" />
                  <span>
                    <strong>Rule:</strong> If cycle start day is <strong>{form.cycle_start_day}</strong> and cycle end day is <strong>{form.cycle_end_day}</strong>, 
                    each monthly period runs from the {form.cycle_start_day}th of the month through the {form.cycle_end_day}th of the subsequent month.
                    Shorter months and February leap days (28/29) are clamped safely.
                  </span>
                </div>
              </div>

              {/* Credit Amount & Unit */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Permission Credit Amount *
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    required
                    value={form.credit_amount}
                    onChange={(e) => setForm({ ...form, credit_amount: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <span className="text-[11px] text-slate-400">Credits accrued per employee per cycle</span>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Credit Unit *
                  </label>
                  <select
                    value={form.credit_unit}
                    onChange={(e) => setForm({ ...form, credit_unit: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="permissions">Permissions (Count)</option>
                    <option value="hours">Hours</option>
                  </select>
                  <span className="text-[11px] text-slate-400">Unit of balance accounting</span>
                </div>
              </div>

              {/* Effective From / To Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Effective From
                  </label>
                  <input
                    type="date"
                    value={form.effective_from}
                    onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Effective To
                  </label>
                  <input
                    type="date"
                    value={form.effective_to}
                    onChange={(e) => setForm({ ...form, effective_to: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>

              {/* Status Toggle */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                <input
                  type="checkbox"
                  id="calendar-is-active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4 text-[#0052FF] rounded border-slate-300 focus:ring-0 cursor-pointer"
                />
                <label htmlFor="calendar-is-active" className="text-sm font-semibold text-slate-800 cursor-pointer">
                  Set as Active Calendar for this Company
                </label>
              </div>

              {/* Audit / Change Reason (Required when editing active calendar) */}
              {editingCalendar && editingCalendar.is_active && (
                <div className="p-3.5 bg-amber-50 rounded-xl border border-amber-200 space-y-1.5">
                  <label className="block text-xs font-bold text-amber-900 uppercase">
                    Audit Log: Change Reason *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Describe why calendar configuration or dates are changing..."
                    value={form.change_reason}
                    onChange={(e) => setForm({ ...form, change_reason: e.target.value })}
                    className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white"
                  />
                  <span className="text-[11px] text-amber-700">
                    Required by audit compliance. Historical credits will not be rewritten.
                  </span>
                </div>
              )}

              {/* Modal Actions */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowConfigModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#0052FF] hover:bg-blue-600 text-white rounded-xl text-sm font-semibold shadow-sm"
                >
                  {editingCalendar ? 'Update Calendar' : 'Create Calendar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 2: Schedule Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full border border-slate-100 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#0052FF]" />
                  Upcoming Credit Schedule Preview
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">{previewCalName}</p>
              </div>
              <button onClick={() => setShowPreviewModal(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
              <p className="text-xs text-slate-500">
                Below are the next 6 monthly cycles calculated dynamically based on this calendar's start/end rules:
              </p>
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                {previewSchedule.map((s, idx) => (
                  <div
                    key={idx}
                    className={`p-3.5 flex items-center justify-between text-xs transition-colors ${
                      s.status === 'current_active'
                        ? 'bg-emerald-50/70 border-l-4 border-l-emerald-500'
                        : s.status === 'outside_period'
                        ? 'bg-slate-50 text-slate-400'
                        : 'bg-white'
                    }`}
                  >
                    <div>
                      <div className="font-semibold text-slate-800 text-sm">{s.cycle_label}</div>
                      <div className="text-slate-500 mt-0.5">
                        Credit Date: <span className="font-medium text-slate-700">{s.credit_date}</span> • Cycle Key: <span className="font-mono">{s.cycle_key}</span>
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <span className="inline-block px-2 py-0.5 rounded font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 text-xs">
                        +{s.credit_amount} {s.unit}
                      </span>
                      <div>
                        {s.status === 'current_active' && (
                          <span className="text-[10px] font-bold text-emerald-700 uppercase bg-emerald-100 px-1.5 py-0.5 rounded">
                            Current Cycle
                          </span>
                        )}
                        {s.status === 'upcoming' && (
                          <span className="text-[10px] font-medium text-blue-600 uppercase bg-blue-50 px-1.5 py-0.5 rounded">
                            Upcoming
                          </span>
                        )}
                        {s.status === 'outside_period' && (
                          <span className="text-[10px] font-medium text-slate-400 uppercase bg-slate-100 px-1.5 py-0.5 rounded">
                            Outside Accrual Period
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 text-right">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-slate-800 text-white rounded-xl text-xs font-semibold hover:bg-slate-700"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 3: History / Audit Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full border border-slate-100 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-700" />
                  Configuration Change History
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">{previewCalName}</p>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
              {selectedHistory.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-xs">
                  No previous changes recorded. This configuration is at initial version.
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedHistory.map((h, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-1">
                      <div className="flex items-center justify-between text-slate-500">
                        <span className="font-semibold text-slate-700 uppercase">{h.action || 'Updated'}</span>
                        <span>{h.changed_at ? new Date(h.changed_at).toLocaleString() : 'N/A'}</span>
                      </div>
                      <div className="text-slate-800">
                        <strong>User:</strong> {h.user_name || h.user_id || 'System'}
                      </div>
                      {h.reason && (
                        <div className="text-slate-600 italic">
                          <strong>Reason:</strong> "{h.reason}"
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 text-right">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-slate-800 text-white rounded-xl text-xs font-semibold hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 4: Run Accrual Now Modal */}
      {showRunAccrualModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-slate-100 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Play className="w-4 h-4 text-emerald-600" />
                  Trigger Monthly Permission Accrual
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Execute permission credits for all active company employees under the configured calendar.
                </p>
              </div>
              <button onClick={() => setShowRunAccrualModal(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                  Target Accrual Reference Date
                </label>
                <input
                  type="date"
                  value={accrualTargetDate}
                  onChange={(e) => setAccrualTargetDate(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                />
                <span className="text-[11px] text-slate-400">
                  The engine will resolve the exact cycle for this date and issue credits once per employee.
                </span>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900 space-y-1">
                <div className="font-semibold flex items-center gap-1.5">
                  <ShieldAlert className="w-4 h-4 text-[#0052FF]" />
                  Duplicate Protection Guarantee
                </div>
                <p className="text-blue-800">
                  Rerunning this process is 100% idempotent. Employees who have already received credit for this cycle will be skipped safely without creating duplicate balances.
                </p>
              </div>

              {accrualRunResult && (
                <div
                  className={`p-4 rounded-xl border text-xs space-y-2 ${
                    accrualRunResult.success
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                      : 'bg-amber-50 border-amber-200 text-amber-900'
                  }`}
                >
                  <div className="font-bold text-sm">{accrualRunResult.message}</div>
                  <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-emerald-200/50">
                    <div>
                      <span className="text-slate-500 block">Processed</span>
                      <span className="font-bold text-base">{accrualRunResult.processed_count || 0}</span>
                    </div>
                    <div>
                      <span className="text-emerald-700 block">Credited</span>
                      <span className="font-bold text-base text-emerald-700">{accrualRunResult.credited_count || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Skipped/Duplicate</span>
                      <span className="font-bold text-base">{accrualRunResult.skipped_count || 0}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowRunAccrualModal(false)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl text-xs font-semibold hover:bg-slate-100"
              >
                Close
              </button>
              <button
                type="button"
                disabled={accrualRunning}
                onClick={handleExecuteAccrual}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm disabled:opacity-50"
              >
                {accrualRunning ? 'Executing Accrual...' : 'Execute Accrual Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
